/* Flagging detected forms, fields & clusters as well as ignored
 * elements is necessary to improve the performance of the detectors
 * when ran multiple times against the same page */
import type { Fnode } from "@protontech/fathom";
import { FORM_CLUSTER_ATTR, kFieldSelector } from "../constants/selectors";
import { FieldType, FormType } from "../types";
import { or } from "./combinators";
import { closestParent } from "./dom";

export const isCluster = (el: HTMLElement) => el.getAttribute(FORM_CLUSTER_ATTR) !== null;
export const flagCluster = (el: HTMLElement) => el.setAttribute(FORM_CLUSTER_ATTR, "");

type FlaggedElement = HTMLElement & {
  __PP_TYPE__?: string /* cached predicted type */;
  __PP_SUBTYPE__?: string /* cached sub-type */;
  __PP_SKIP__?: boolean /* excluded from detection pipeline */;
  __PP_SEEN__?: boolean /* flagged as processed for detection triggers */;
  __PP_HIDDEN__?: boolean /* flagged as a hidden node */;
};

export const isHidden = (el: HTMLElement) => (el as FlaggedElement).__PP_HIDDEN__ === true;
export const flagAsHidden = (el: HTMLElement) => ((el as FlaggedElement).__PP_HIDDEN__ = true);
export const removeHiddenFlag = (el: HTMLElement) => delete (el as FlaggedElement).__PP_HIDDEN__;

export const attrIgnored = (el: HTMLElement) => el.getAttribute("data-protonpass-ignore") !== null;
export const isIgnored = (el: HTMLElement) => (el as FlaggedElement).__PP_SKIP__ === true;
export const getIgnoredParent = (el?: HTMLElement) => (el ? closestParent(el, isIgnored) : null);
export const flagAsIgnored = (el: HTMLElement) => ((el as FlaggedElement).__PP_SKIP__ = true);
export const removeIgnoredFlag = (el: HTMLElement) => delete (el as FlaggedElement).__PP_SKIP__;
export const flagSubtreeAsIgnored = (el: HTMLElement) => {
  flagAsIgnored(el);
  el.querySelectorAll<HTMLElement>(kFieldSelector).forEach(flagAsIgnored);
};

export const isProcessed = (el: HTMLElement) => (el as FlaggedElement).__PP_SEEN__ === true;
export const flagAsProcessed = (el: HTMLElement) => ((el as FlaggedElement).__PP_SEEN__ = true);
export const removeProcessedFlag = (el: HTMLElement) => delete (el as FlaggedElement).__PP_SEEN__;

export const isPrediction = (el: HTMLElement) => (el as FlaggedElement).__PP_TYPE__ !== undefined;
export const removePredictionFlag = (el: HTMLElement) => {
  delete (el as FlaggedElement).__PP_TYPE__;
  delete (el as FlaggedElement).__PP_SUBTYPE__;
};
export const getParentFormPrediction = (el?: HTMLElement) => (el ? closestParent(el, isPrediction) : null);

const TYPE_SEPARATOR = ",";
const SCORE_SEPARATOR = ":";

export const setCachedSubType = (_el: HTMLElement, subType: string) => {
  const el = _el as FlaggedElement;
  el.__PP_SUBTYPE__ = subType;
};

export const getCachedSubType = (el: HTMLElement): string | undefined => (el as FlaggedElement).__PP_SUBTYPE__;

export const matchPredictedType =
  (type: string) =>
  (str: string): boolean =>
    str.split(SCORE_SEPARATOR)?.[0] === type;

/** Sets or updates a prediction score for a specific type on an DOM node.
 * If the type already exists, it updates the score. If not, it adds a new
 * `type:score` pair. Multiple prediction results are separated by commas. */
export const setCachedPredictionScore = (_el: HTMLElement, type: string, score: number): void => {
  const el = _el as FlaggedElement;
  const currentType = el.__PP_TYPE__;
  const flag = `${type}${SCORE_SEPARATOR}${score.toFixed(2)}`;

  if (!currentType) {
    el.__PP_TYPE__ = flag;
    return;
  }

  const types = currentType.split(TYPE_SEPARATOR);
  const existingIndex = types.findIndex(matchPredictedType(type));

  if (existingIndex !== -1) types[existingIndex] = flag;
  else types.push(flag);

  el.__PP_TYPE__ = types.join(TYPE_SEPARATOR);
};

/** Retrieves the cached prediction score for a specific type from an
 * Fnode. If the type or score is not found, it returns `-1`. */
export const getCachedPredictionScore =
  (type: string) =>
  (fnode: Fnode): number => {
    const types = (fnode.element as FlaggedElement).__PP_TYPE__;
    if (!types) return -1;

    const predForType = types.split(TYPE_SEPARATOR).find(matchPredictedType(type));
    if (!predForType) return -1;

    const [, scoreStr] = predForType.split(SCORE_SEPARATOR);
    const score = parseFloat(scoreStr);
    return Number.isFinite(score) ? score : -1;
  };

export const isPredictedType =
  (type: string) =>
  (fnode: Fnode): boolean =>
    getCachedPredictionScore(type)(fnode) !== -1;

export const isPredictedForm = or(...Object.values(FormType).map((type) => isPredictedType(type)));
export const isPredictedField = or(...Object.values(FieldType).map((type) => isPredictedType(type)));
export const isClassifiable = (el: HTMLElement) => !(isPrediction(el) || isIgnored(el) || attrIgnored(el));

/** Removes every flag on an element and its field descendants.
 * The `removeIgnored` option controls how ignored flags should
 * be cleaned-up. Setting it to `false`  */
export const removeClassifierFlags = (target: HTMLElement, options: { preserveIgnored: boolean; fields?: HTMLElement[] }): void => {
  const clean = (el: HTMLElement) => {
    removeProcessedFlag(el);
    removePredictionFlag(el);
    if (!options.preserveIgnored) removeIgnoredFlag(el);
  };

  clean(target);
  target.querySelectorAll<HTMLElement>(kFieldSelector).forEach(clean);
  options.fields?.forEach(clean);
};
